export type Env = 'live' | 'uat' | 'dev';

/**
 * Which copy of the board this is.
 *
 * The build flag is the intended answer, but it is only as reliable as the
 * command someone typed. The hostname is the fact: the real board is served
 * from exactly one domain, and every preview channel Firebase creates has a
 * `--` in its host. So a build mislabelled as live still announces itself as a
 * test copy when it is not actually being served from the live address — the
 * failure that matters is a test copy pretending to be real, never the reverse.
 */
export function currentEnv(): Env {
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  /*
   * WHERE THE REAL ONES LIVE. The Firebase-backed original, and — from 13
   * August — the Postgres one real testers work in. Both are named because the
   * comment below turned out to be right: `app.flightdeck.build` landed in the
   * catch-all and would have announced itself as a test copy to the people
   * least able to tell.
   *
   * A build still has to AGREE it is live. Host alone would mean a UAT bundle
   * accidentally deployed to a production hostname announced itself as the real
   * thing, which is the direction that matters.
   */
  const REAL = [
    'flightdeck-vsnl.web.app',
    'flightdeck-vsnl.firebaseapp.com',
    'app.flightdeck.build',
    'flightdeck-live-app.web.app',
    'flightdeck-live-app.firebaseapp.com',
    /*
     * AND THE OPERATOR PORTAL, which is exactly the omission the note below
     * warns about. The portal reads this to decide whether to paint the band
     * that says "real customers" or the striped one that says "nothing here is
     * real" — so leaving these out would give the live portal, the one that
     * creates actual customer accounts, the reassuring stripes of a test copy.
     */
    'admin.flightdeck.build',
    'flightdeck-live-admin.web.app',
    'flightdeck-live-admin.firebaseapp.com',
  ];
  if (REAL.includes(host)) {
    return __ENV__ === 'uat' ? 'uat' : 'live';
  }
  /*
   * LOCALHOST IS "DEV" ONLY IF IT IS ALSO POINTED AT TEST DATA.
   *
   * This returned 'dev' for localhost unconditionally, and the banner it feeds
   * then says "Running on a development machine, against the test data." On 16
   * August that sentence was shown by a build stamped `live` — which reads and
   * writes the REAL database — while verifying the migration locally. The screen
   * stated the exact opposite of the truth, in the one place a person looks to
   * find out which copy they are about to type into.
   *
   * The rule above is that the hostname is the fact and a build has to AGREE it
   * is live, because a test copy pretending to be real is the failure that
   * matters. That still holds for a deployed site. It does not hold here: on a
   * laptop the hostname says nothing about the database, only the stamp does,
   * and the dangerous direction is reversed — somebody clicking around what they
   * believe is a test copy while every write lands on real people's work.
   */
  if (host === 'localhost' || host === '127.0.0.1') return __ENV__ === 'live' ? 'live' : 'dev';
  /*
   * ANYTHING ELSE IS A TEST COPY, and that is the safe way round — a test copy
   * mislabelled as live is the failure that matters, never the reverse.
   *
   * uat.flightdeck.build lands here and is correct without naming it. WHEN LIVE
   * MOVES TO ITS OWN DOMAIN the live check above must be told about it in the
   * same change, or live will quietly announce itself as UAT. Named here
   * because that is a one-line omission with a very confusing symptom.
   */
  return 'uat';
}

/**
 * SHOW A SCREEN THAT CANNOT BE REACHED ON PURPOSE.
 *
 *   https://uat.flightdeck.build/?show=stale
 *
 * WHY THIS EXISTS. Some states can only happen to somebody, never be arranged:
 * the out-of-date sign-in needs a token minted before a schema change, and the
 * moment anybody signs in again it is gone. So it could be proved by the tests
 * and never once looked at by a person — and "the wording is right" is not a
 * thing a test can tell you.
 *
 * NEVER ON LIVE, and that is the whole design of it. The check is `currentEnv()
 * !== 'live'`, which is decided by the hostname first and only agrees it is live
 * when the build agrees too — so this cannot be turned on by a query string, a
 * stored value, or anything a browser can be talked into. The worst somebody
 * can do with the address below is see a screen on a test copy.
 *
 * It forces the SCREEN, not the condition. Nothing about the real detection
 * runs, which is worth knowing before signing anything off on the strength of
 * having seen it.
 */
export function forced(name: string): boolean {
  if (typeof window === 'undefined') return false;
  if (currentEnv() === 'live') return false;
  return new URLSearchParams(window.location.search).get('show') === name;
}

export const ENV_LABEL: Record<Env, string> = {
  live: 'Live',
  uat: 'UAT',
  dev: 'Local',
};

/** Version, commit and environment in one string, for tooling that reads the built bundle. */
export const BUILD_INFO = __BUILD_INFO__;

/**
 * What to write down when something has to say which build it came from.
 *
 * The commit matters more than the version and is the reason this is not just
 * the number: two builds can share a version during development and never
 * share a commit, so this is what pins a report to exact code.
 */
export const BUILD_LABEL = `v${__APP_VERSION__} · ${__BUILD_COMMIT__}`;
